import type {
  ClientProfileContent as SharedClientProfileContent,
  DocumentItem,
  FormUserType,
} from "@smartwiz/shared-types";

export type {
  ClientProfileResponse,
  DocumentItem,
  OCRExtraction,
  OCRState,
  FormUserType,
  DocumentCategory,
  ClientFieldError,
  FieldError,
  OCRError,
  FieldErrorDocInfo,
} from "@smartwiz/shared-types";

/** Legacy alias retained for document owner fields across the CRM profile UI. */
export type DocumentUserType = FormUserType;

export interface ClientProfileContent extends Omit<
  SharedClientProfileContent,
  "header" | "body"
> {
  header: Header;
  body: ProfileBody;
}

export type ProfileTabs = "taxinfo" | "email";
export const isProfileTab = (s: string | null): ProfileTabs => {
  if (!s) {
    return "taxinfo";
  }
  if (["taxinfo", "email"].includes(s)) {
    return s as ProfileTabs;
  }
  return "taxinfo";
};

export interface Header {
  basicInfo: BasicInfo;
  assignee: Assignee;
  status: Status;
  organizerStatus: OrganizerStatus;
  difficultyScore: DifficultyScore;
  smartsync: Smartsync;
}

export interface BasicInfo {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  dateOfBirth: string;
  email: string;
}

export interface Assignee {
  firstName: string;
  lastName: string;
  userId: string;
}

export interface Status {
  status: string;
}

export interface OrganizerStatus {
  status: string;
  submitDate: string;
  updateDate: string;
}

export interface DifficultyScore {
  score: number;
  label: string;
}

export interface Smartsync {
  active: boolean;
  taxYear: number;
  activeDate: string;
}

// ================= BODY =================

export interface ProfileBody {
  mainInfo: MainInfo;
  incomeSources: IncomeSources;
  business: Business;
  // Raw taxpayer-submitted businesses from intake — same shape as `business`
  // but all fields are read-only. Shown so tax pros can see pre-entered data
  // that may not have flowed into the canonical Businesses section yet.
  organizerBusinesses?: Business;
  creditsAndDeductions: CreditsAndDeductions;
  additionalQuestions: SectionWithFieldMap;
  comments: SectionWithFieldMap;
  availableYears?: number[];
}

// ---- Shared field structures ----

export interface LabelValueField {
  label: string;
  value: string | number | boolean;
}

export interface FieldMap {
  [fieldName: string]: LabelValueField;
}

export interface SectionWithFieldMap {
  key: string;
  label: string;
  fields: FieldMap;
}

// ---- Main Info ----

export interface MainInfo {
  key: string;
  label: string;
  taxClient: TaxClient;
  spouseInformation: SectionWithFieldMap;
  dependents: Dependents;
  bankAccountInformation: SectionWithFieldMap;
}

export interface TaxClient {
  key: string;
  label: string;
  contactInfo: SectionWithFieldMap;
  sensitiveInfo: SectionWithFieldMap;
  taxClientInformation: SectionWithFieldMap;
  residences: ResidenceGroup;
}

export interface ResidenceGroup {
  key: string;
  label: string;

  [residenceId: string]: ResidenceSection | string;
}

export interface ResidenceSection {
  key: string;
  label: string;
  fields: FieldMap;
}

export interface Dependents {
  key: string;
  label: string;

  [dependentId: string]: Dependent | string;
}

export interface Dependent {
  key: string;
  label: string;
  fields: FieldMap;
}

// ---- Income Sources ----

export interface IncomeSources {
  key: string;
  label: string;
  taxClientIncomeInformation: IncomeInformation;
  spouseIncomeInformation: IncomeInformation;
}

export interface IncomeInformation {
  key: string;
  label: string;
  sourceOfIncome: IncomeGroup;
  otherSourceOfIncome: IncomeGroup;
}

export interface IncomeGroup {
  key: string;
  label: string;

  [incomeId: string]: IncomeItem | string;
}

export interface IncomeItem {
  key: string;
  label: string;
  fields: FieldMap;
}

// ---- Business ----

export interface Business {
  key: string;
  label: string;
  businessBasics: SectionWithFieldMap;
  businessAddress: SectionWithFieldMap;
  businessExpenses: BusinessItemGroup;
  businessDocuments: BusinessItemGroup;
}

export interface BusinessItemGroup {
  key: string;
  label: string;

  [itemId: string]: BusinessItem | string;
}

export interface BusinessItem {
  key: string;
  label: string;
  fields: FieldMap;
}

// ---- Credits & Deductions ----

export interface CreditsAndDeductions {
  key: string;
  label: string;
  charitableGiving: DocumentGroupMap;
  charityDocuments: DocumentGroupMap;
  medicalExpenses: DocumentGroupMap;
  medicalDocuments: DocumentGroupMap;
  creditsAndDeductionsDocuments: DocumentGroupMap;
}

export interface DocumentGroupMap {
  key: string;
  label: string;

  [itemId: string]: BusinessItem | string;
}

// ---- Documents Array ----
/**
 * @deprecated Use flat DocumentItem[] instead. Document grouping is now handled on the frontend.
 * Displays a group of documents in the sidebar of the client profile.
 */
export interface DocumentGroup {
  /**
   * Unique key used to rendering purposes.
   */
  key: string;
  /**
   * The label to display for this group.
   */
  label: string;
  /**
   * The document type of the group.
   */
  documentType: string;
  /**
   * The documents in this group.
   */
  items: DocumentItem[];
}

export interface DocumentSection {
  key: string;
  label: string;
  documentType: string;
  items: DocumentItem[];
}

export interface Section {
  key: string;
  label: string;
}

// Types
export interface Section {
  key: string;
  label: string;
}

export interface FieldObject {
  label: string;
  value: string | number | boolean;
  key?: string;
  reviewMode?: any;
}

export type ContentItem =
  | {
      type: "label";
      text: string;
      indentLevel: number;
      key: string;
      level: "top" | "sub" | "subsub";
    }
  | {
      type: "field";
      label: string;
      value: string | number | boolean;
      indentLevel: number;
      key: string;
      reviewMode?: any;
    }
  | {
      type: "document";
      text?: string;
      presignedUrl?: string;
      indentLevel: number;
      key: string;
      label: string;
      fileType: string;
      url: string;
    }
  | {
      type: "dynamic_items";
      key: string;
      label: string;
      items: any[];
      indentLevel: number;
    };

export interface SelectedDoc {
  presignedUrl: string | null;
  label: string | null;
  key: string | null;
  userType: FormUserType | null;
  docId?: string | null;
}

// Review Items

export type FieldType =
  | "text"
  | "email"
  | "date"
  | "number"
  | "money"
  | "password"
  | "select"
  | "multiselect";

export interface ReviewMode {
  /**
   * The type of field.
   */
  fieldType: FieldType;
  /**
   * Whether this field is required.
   */
  required: boolean;
  /**
   * The placeholder to show in field if no value set.
   */
  placeholder?: string;
  /**
   * The options to select from if type == select.
   */
  options?: string[];
  /**
   * The validator to use on this field.
   */
  validator?: string;
}

export interface ReviewItemData {
  type: string;
  key: string;
  label: string;
  value: any;
  reviewMode?: ReviewMode;
  indentLevel: number;
}

export interface ReviewItemProps {
  item: ReviewItemData;
}

export interface FieldComponentProps {
  commonProps: CommonFieldProps;
  field: {
    value: any;
    onChange: (value: any) => void;
    onBlur: () => void;
    name: string;
    ref: React.Ref<any>;
  };
  fieldType?: FieldType;
  options?: string[];
  item: ReviewItemData;
}

export interface CommonFieldProps {
  name: string;
  label: string;
  placeholder: string;
  required?: boolean;
  size: "md";
  error?: boolean;
  disabled?: boolean;
  rightSection?: React.ReactNode;
  styles?: any;
  classNames?: any;
}

export interface UpdatePayload {
  key: string;
  value: any;
}

//OCR Fields
export interface OCRResponse {
  message: string;
  content: OCRFormData;
}

export interface OCRFormData {
  key: string;

  // Reuse Section concept but with OCR fields
  [sectionName: string]: OCRSection | string;
}

export interface OCRSection extends Section {
  fields?: Record<string, OCRField>;
  items?: OCRItem[];
  // Additional OCR-specific metadata
  userType?: string;
  type?: string;
}

// Modified version of your FieldObject with reviewMode
export interface OCRField {
  key: string;
  label: string;
  value: string | number | boolean;
  reviewMode: ReviewMode; // Reuse your existing ReviewMode
}

export interface OCRItem {
  key: string;
  label: string;
  fields: Record<string, OCRField>;
}

export interface StartOCRResponse {
  jobId: string;
  docId: number;
}
